import { EnhancedStore } from '@reduxjs/toolkit';
import { notificationActions } from 'redux/notification/notification.slice';
import { Problem } from 'types/entity';
import { History } from 'history';
import { getLocalStorageReturnUrl, setLocalStorageReturnUrl } from 'utils/FpLocalStorage/local-storage';
import { AppDispatch } from 'redux/store';
import { appInsights } from 'azure-application-insights';

// エラー画面へ遷移させるError Type
const targetTypes: string[] = [
  '/error/global/0000',
  '/error/global/0001',
  '/error/global/0002',
  '/error/global/0003',
  '/error/global/0004',
  '/error/global/0005',
  '/error/global/0006',
  '/error/global/0007',
  '/error/global/0008',
  '/error/S-CAPI-FP-001002/0002',
  '/error/S-CAPI-FP-001002/0004',
  '/error/S-CAPI-FP-001002/0005',
  '/error/S-CAPI-FP-001002/0003',
  '/error/S-CAPI-FP-001006/0002',
];

const layoutNotFoundError: string[] = [
  '/error/S-CAPI-FP-002005/0002',
];

const ignorableError: string[] = [
  '/error/S-CAPI-FP-002006/0002',
];

/**
 * /callback = ログイン後にプロジェクト取得でグローバルエラーが発生する場合はエラータイプを読み替える
 * @param path URLパス
 * @param type エラータイプ
 */
const getErrorType = (path: string, type?: string): string => {
  if (path === '/callback') {
    switch (type) {
      case '/error/global/0003':
      case '/error/global/0004':
      case '/error/global/0005':
        if (process.env.NODE_ENV === 'development') {
          console.log('recover global error', type);
        }
        return '/error/S-CAPI-FP-001006/0002';
    }
  }
  return type || '/error/global/0000';
};

/**
 * リクエストエラーハンドラ.
 * エラー種別、発生した画面、URLなどからエラーに対する応答を決める
 * @param error エラーレスポンス
 * @param store ストア。notificationに利用
 * @param history 現在のパスを取得するのに使用
 */
const setError = (error: Problem | undefined, store: EnhancedStore, history: History) => {
  const status = error?.status || 500;
  const path = history.location.pathname;
  const type = getErrorType(path, error?.type);
  const isErrorPage: boolean = targetTypes.includes(type);
  const dispatch = store.dispatch as AppDispatch;

  // 通常はNotificationを出すだけ
  if (!isErrorPage) {
    const ignorable = ignorableError.includes(type);
    if (ignorable) {
      return;
    }
    // レイアウト０件は警告メッセージ
    const layoutNotFound: boolean = layoutNotFoundError.includes(type);
    if (layoutNotFound) {
      dispatch(notificationActions.addWarning({ messageId: error?.type || '' }));
    } else {
      dispatch(notificationActions.addApiError(error));
    }
    return;
  }

  // portal画面で発生する404, 500エラーはエラー画面に遷移させない
  const continueErrorsAtPortal = [404, 500];
  if (path === '/portal' && continueErrorsAtPortal.includes(status || -1)) {
    dispatch(notificationActions.addApiError(error));
    return;
  }

  // 認証の途中だったらリダイレクト先を変更する
  const errorUrl = `/error?status=${status}&type=${type}`;
  const returnUrlExists = !!getLocalStorageReturnUrl();
  if (returnUrlExists) {
    setLocalStorageReturnUrl(errorUrl);
    return;
  }
  // エラーページへリダイレクトする
  history.push(errorUrl);
};

/**
 * send track event when http error.
 * @param properties metrics
 */
const reportError = (properties: {[key: string]: any}) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('FpDependencyError', properties);
  }
  appInsights.trackTrace({ message: 'FpRequestError', properties });
  appInsights.trackEvent({ name: 'FpDependencyError' }, properties);
};

export {
  setError,
  reportError,
};
